import uniq from 'lodash/uniq';
import * as React from 'react';
import { emptyArray } from 'vet-bones/constants';
import { noop } from 'vet-bones/utils';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { refreshDatabase } from 'src/actions/databases/action-creators/refreshDatabase';
import { refreshDatabases } from 'src/actions/databases/action-creators/refreshDatabases';
import { exportProject } from 'src/actions/projects/action-creators/exportProject';
import { publishProject } from 'src/actions/projects/action-creators/publishProject/publishProject';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  generateFileUploadNamedGraph,
  generateModelNamedGraph,
  generateVirtualGraphName,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import {
  CreationType,
  CsvMappingInfo,
  CsvMappingInfoByProjectFileUploadId,
  DatabaseInfo,
  ModelAliasInfo,
  ModelInfo,
  ModelsInfo,
  PrefixInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
  VirtualGraphMappingInfo,
  VirtualGraphMappingInfoByProjectVgId,
} from 'src/containers/PublishProjectDrawer/types';
import { catalogDatabaseSelector } from 'src/selectors/catalogDatabase';
import { connectionSelector } from 'src/selectors/connection';
import { databaseItemsSelector } from 'src/selectors/databaseItems';
import { databaseSensitivePropertyGroupsSelector } from 'src/selectors/databaseSensitivePropertyGroups';
import { dataModelItemsSelector } from 'src/selectors/dataModelItems';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { modelItemsSelector } from 'src/selectors/modelItems';
import { namedGraphItemsSelector } from 'src/selectors/namedGraphItems';
import { namedGraphsSelector } from 'src/selectors/namedGraphs';
import { projectSelector } from 'src/selectors/project';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { projectSensitivePropertyGroupsByNameSelector } from 'src/selectors/projectSensitivePropertyGroupsByName';
import { projectVirtualGraphsByIdSelector } from 'src/selectors/projectVirtualGraphsById';
import { virtualGraphItemsSelector } from 'src/selectors/virtualGraphItems';
import { connectionAsyncActions } from 'src/store/connections/connections.slice';
import {
  ProjectFileUpload,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { getDanglingNamedGraphs } from 'src/utils/getDanglingNamedGraphs';
import {
  useDesignerPermissions,
  useVerifyPublishPermissions,
} from 'src/utils/hooks/useDesignerPermissions';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  INVALID_IRI_MATCHER,
  IRI_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidIriChars';
import {
  INVALID_NAME_MATCHER,
  NAME_STARTS_WITH_MATCHER,
  replaceInvalidNameChars,
} from 'src/utils/removeInvalidNameChars';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';

// default info

export const useDefaultModelsInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const modelsById = useReduxSelector(projectModelsByIdSelector(projectId));
  const findModel = useFindModel(databaseId);

  return React.useMemo<ModelsInfo>(() => {
    if (!project) {
      return {};
    }

    const { modelHistoriesById } = project.publishHistory;
    const modelsInfo = Object.values(modelsById).reduce<ModelsInfo>(
      (acc, model) => {
        const { id: projectModelId } = model;

        const modelHistory = modelHistoriesById[model.id] as
          | Pick<ModelInfo, 'modelName' | 'namedGraphIri' | 'shouldPublish'>
          | undefined;
        const shouldPublish = modelHistory?.shouldPublish ?? true;

        let creationType = CreationType.NEW;
        let modelName = replaceInvalidNameChars(model.name);
        let namedGraphIri = generateModelNamedGraph(project, model);

        if (modelHistory) {
          modelName = modelHistory.modelName;
          namedGraphIri = modelHistory.namedGraphIri;
        }

        // use the first named graph iri of the model on the server if it exists
        const modelOnServer = findModel(modelName);
        if (modelOnServer) {
          creationType = CreationType.REPLACE;
          if (modelOnServer.namedGraphIris.length) {
            [namedGraphIri] = modelOnServer.namedGraphIris;
          }
        }

        acc[model.id] = {
          creationType,
          modelName,
          namedGraphIri,
          projectModelId,
          shouldPublish,
        };
        return acc;
      },
      {}
    );

    return modelsInfo;
  }, [findModel, modelsById, project]);
};

export const useModelAliasNamePlaceholder = () => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  return React.useMemo(() => {
    if (!project) {
      return '';
    }
    const defaultModelName = replaceInvalidNameChars(
      `${project.name}_data_model_combo`
    );
    return defaultModelName;
  }, [project]);
};

export const useDefaultModelAliasInfo = (
  databaseId: string,
  modelsInfo: ModelsInfo
) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const dataModelItems = useReduxSelector(dataModelItemsSelector(databaseId));
  const namedGraphs = useReduxSelector(namedGraphsSelector(databaseId));
  const findModel = useFindModel(databaseId);
  const namePlaceholder = useModelAliasNamePlaceholder();

  return React.useMemo<ModelAliasInfo>(() => {
    if (!project) {
      return {
        creationType: CreationType.NEW,
        modelName: '',
        namedGraphIris: emptyArray as any,
      };
    }

    const { modelAliasHistory } = project.publishHistory;

    let creationType = CreationType.NEW;
    let modelName = namePlaceholder;
    // select all publishing models' named graph iris
    let namedGraphIris = Object.values(modelsInfo).reduce<string[]>(
      (acc, modelInfo) => {
        if (modelInfo.shouldPublish) {
          acc.push(modelInfo.namedGraphIri);
        }
        return acc;
      },
      []
    );

    if (modelAliasHistory) {
      modelName = modelAliasHistory.modelName;
    }

    // select the named graph iris of the model on the server if it exists
    const modelOnServer = findModel(modelName);
    if (modelOnServer) {
      creationType = CreationType.REPLACE;
      if (modelOnServer.namedGraphIris.length) {
        namedGraphIris = modelOnServer.namedGraphIris;
      }
    }

    // filter out dangling named graphs if we are replacing a data model
    if (dataModelItems.find(({ value }) => value === modelName)) {
      namedGraphIris = namedGraphIris.filter((namedGraphIri) => {
        const dangling = getDanglingNamedGraphs({
          dataModelItems,
          modelsInfo,
          modelAliasInfo: {
            creationType: CreationType.REPLACE,
            modelName,
            namedGraphIris: [],
          },
          namedGraphs,
        });
        // don't select the graphs that would be removed
        if (dangling[modelName] === namedGraphIri) {
          return false;
        }

        return true;
      });
    }

    return {
      creationType,
      modelName,
      namedGraphIris,
    };
  }, [
    dataModelItems,
    findModel,
    modelsInfo,
    namePlaceholder,
    namedGraphs,
    project,
  ]);
};

export const useDefaultSPGInfo = (databaseId: string | null) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const sensitivePropertyGroupsByName = useReduxSelector(
    projectSensitivePropertyGroupsByNameSelector(projectId)
  );
  const databaseSensitivePropertyGroupsByName = useReduxSelector(
    databaseSensitivePropertyGroupsSelector(databaseId)
  );

  return React.useCallback(() => {
    const sensitivePropertyGroupsForPublish: SensitivePropertyGroupsInfo = {};

    Object.keys(sensitivePropertyGroupsByName).forEach((name) => {
      const attributes = sensitivePropertyGroupsByName[name];
      if (attributes.length < 1) {
        return;
      }
      const history =
        project?.publishHistory.sensitivePropertyGroupsByName[name];

      const databaseSpg =
        databaseSensitivePropertyGroupsByName[history?.publishName || name];
      let actionType: CreationType;
      if (databaseSpg) {
        if (!history?.actionType || history?.actionType === CreationType.NEW) {
          actionType = CreationType.APPEND;
        } else {
          actionType = history.actionType;
        }
      } else {
        actionType = CreationType.NEW;
      }
      sensitivePropertyGroupsForPublish[name] = {
        spgName: name,
        publishName: history?.publishName || name,
        actionType,
        attributes,
      };
    });
    return sensitivePropertyGroupsForPublish;
  }, [
    sensitivePropertyGroupsByName,
    project,
    databaseSensitivePropertyGroupsByName,
  ]);
};

export const useDefaultCsvMappingInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const findNamedGraph = useFindNamedGraph(databaseId);
  return React.useCallback(
    (fileUpload: ProjectFileUpload): CsvMappingInfo => {
      const history =
        project?.publishHistory.csvResourceHistoriesById[fileUpload.id];

      const namedGraphIri =
        history?.namedGraphIri ||
        (project ? generateFileUploadNamedGraph(project, fileUpload) : '');
      let creationType: CreationType;
      if (findNamedGraph(namedGraphIri)) {
        if (
          !history?.creationType ||
          history.creationType === CreationType.NEW
        ) {
          creationType = CreationType.REPLACE;
        } else {
          creationType = history.creationType;
        }
      } else {
        creationType = CreationType.NEW;
      }
      return {
        creationType,
        fileOptions: null,
        namedGraphIri,
        shouldPublish: history?.shouldPublish ?? true,
      };
    },
    [findNamedGraph, project]
  );
};

export const useDefaultVirtualGraphMappingInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  // Stardog does not allow updating the stardog DB for a VG after creation
  // so we must filter by virtual graphs that are visible for this database
  const findVirtualGraph = useFindVirtualGraph(databaseId);
  const { canPublishVirtualGraphs } = useDesignerPermissions();

  return React.useCallback(
    (virtualGraph: ProjectVirtualGraph): VirtualGraphMappingInfo => {
      if (!project) {
        return {
          creationType: CreationType.NEW,
          projectVgId: virtualGraph.id,
          virtualGraphName: '',
          shouldPublish: true,
        };
      }

      const history =
        project.publishHistory.vgResourceHistoriesById[virtualGraph.id];
      const virtualGraphName =
        history?.virtualGraphName ||
        generateVirtualGraphName(project, virtualGraph);
      const creationType = findVirtualGraph(virtualGraphName)
        ? CreationType.REPLACE
        : CreationType.NEW;
      return {
        creationType,
        projectVgId: virtualGraph.id,
        virtualGraphName,
        shouldPublish: canPublishVirtualGraphs
          ? history?.shouldPublish ?? true
          : false,
      };
    },
    [canPublishVirtualGraphs, findVirtualGraph, project]
  );
};

// databases & data sources

export const useRefreshDatabases = ({
  databaseId,
  setDatabaseId,
}: {
  databaseId: string;
  setDatabaseId: (databaseId: string) => void;
}) => {
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();
  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const didIsConnectedChanged = useDidValueChange(isConnected);

  const databaseItems = useReduxSelector(databaseItemsSelector);
  const isCatalogSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isCatalogSupported', connection)
  );
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);
  const didIsRefreshingDatabasesChanged = useDidValueChange(
    isRefreshingDatabases
  );

  const { projectId } = useRouterParams();

  const catalogDatabaseId = useReduxSelector(catalogDatabaseSelector);
  const project = useReduxSelector(projectSelector(projectId));
  const virtualGraphs = useReduxSelector(
    projectVirtualGraphsByIdSelector(projectId)
  );

  const dataSourceIds = React.useMemo(() => {
    return Object.values(virtualGraphs).reduce<Set<string>>(
      (acc, virtualGraph) => {
        if (typeof virtualGraph.from !== 'string') {
          acc.add(virtualGraph.dataSourceId);
        }

        return acc;
      },
      new Set()
    );
  }, [virtualGraphs]);

  // Update databases and data source type descriptions on connection change
  React.useEffect(() => {
    if (didIsConnectedChanged && isConnected && connection) {
      dispatch(refreshDatabases()).then(() => {
        if (databaseId) {
          dispatch(refreshDatabase(databaseId));
        }
      });
      dataSourcesStardogRequestDispatchers.requestDataSourceTypeDescription(
        connection
      );

      if (isCatalogSupported) {
        dataSourcesStardogRequestDispatchers.requestDataSourceTableColumns({
          databaseId: catalogDatabaseId,
          dataSourceIds: Array.from(dataSourceIds),
        });
      }
    }
  }, [
    catalogDatabaseId,
    connection,
    dataSourceIds,
    databaseId,
    didIsConnectedChanged,
    dispatch,
    isCatalogSupported,
    isConnected,
  ]);

  // Select default database (from publish history), after loading databases
  React.useEffect(() => {
    if (!project) {
      return;
    }
    const { databaseInfo } = project.publishHistory;
    if (
      didIsRefreshingDatabasesChanged &&
      databaseInfo.databaseId &&
      isConnected &&
      !isRefreshingDatabases &&
      databaseItems.find(({ value }) => value === databaseInfo.databaseId)
    ) {
      dispatch(refreshDatabase(databaseInfo.databaseId)).then(() => {
        if (isMounted.current) {
          setDatabaseId(databaseInfo.databaseId);
        }
      });
    }
  }, [
    databaseItems,
    didIsRefreshingDatabasesChanged,
    dispatch,
    isConnected,
    isMounted,
    isRefreshingDatabases,
    project,
    setDatabaseId,
  ]);
};

// model

export const useFindModel = (databaseId: string) => {
  const modelItems = useReduxSelector(modelItemsSelector(databaseId));
  return React.useCallback(
    (modelName: string) => modelItems.find((item) => item.value === modelName),
    [modelItems]
  );
};

export const useModelNamePlaceholderByModelId = (databaseId: string) => {
  const findModel = useFindModel(databaseId);
  const modelsInfo = useDefaultModelsInfo(databaseId);

  return React.useMemo(
    () =>
      Object.entries(modelsInfo).reduce<Record<string, string>>(
        (acc, [modelId, modelInfo]) => {
          const modelNamePlaceholder = findModel(modelInfo.modelName)
            ? copy.components.publishProjectDrawer.modelConfiguration.modelName
                .exampleText
            : replaceInvalidNameChars(modelInfo.modelName);
          acc[modelId] = modelNamePlaceholder;

          return acc;
        },
        {}
      ),
    [findModel, modelsInfo]
  );
};

export const useValidateModelName = ({
  databaseId,
  isAlias,
  isConsolidating,
  modelsInfo,
  targetModelId,
}: {
  databaseId: string;
  isAlias?: boolean;
  isConsolidating?: boolean;
  modelsInfo: ModelsInfo;
  targetModelId?: string;
}) => {
  const findModel = useFindModel(databaseId);

  return React.useCallback(
    (modelInfo: Pick<ModelInfo, 'creationType' | 'modelName'>): string => {
      if (!modelInfo.modelName) {
        return copy.components.publishProjectDrawer.validation.modelName
          .required;
      }

      if (!modelInfo.modelName.match(NAME_STARTS_WITH_MATCHER)) {
        return copy.components.publishProjectDrawer.validation.modelName
          .startsWith;
      }

      const invalidChars = modelInfo.modelName.match(INVALID_NAME_MATCHER);
      if (invalidChars) {
        return copy.components.publishProjectDrawer.validation.modelName.characters(
          uniq(invalidChars)
        );
      }

      // don't allow selecting existing models when creating a new model
      if (
        modelInfo.creationType === CreationType.NEW &&
        findModel(modelInfo.modelName)
      ) {
        return copy.components.publishProjectDrawer.validation.modelName.unique;
      }

      if (modelsInfo) {
        if (isAlias) {
          // on alias page; don't allow selecting models we are publishing
          if (
            Object.values(modelsInfo)
              .filter(({ shouldPublish }) => shouldPublish)
              .map(({ modelName }) => modelName)
              .includes(modelInfo.modelName)
          ) {
            return copy.components.publishProjectDrawer.validation.modelName
              .replacingPublishingModel;
          }
        }

        const usedNames = new Set<string>();
        const isNameUsedMultipleTimes = Object.values(modelsInfo).some(
          (otherModelInfo) => {
            if (!otherModelInfo.modelName) {
              return false;
            }

            if (
              isConsolidating
                ? otherModelInfo.projectModelId !== targetModelId
                : !otherModelInfo.shouldPublish
            ) {
              return false;
            }

            if (
              (isAlias || usedNames.has(otherModelInfo.modelName)) &&
              otherModelInfo.modelName === modelInfo.modelName
            ) {
              return true;
            }

            usedNames.add(otherModelInfo.modelName);
            return false;
          }
        );

        if (isNameUsedMultipleTimes) {
          return copy.components.publishProjectDrawer.validation.modelName
            .usedName;
        }
      }

      return '';
    },
    [findModel, isAlias, isConsolidating, modelsInfo, targetModelId]
  );
};

export const useValidateModelsForSubmission = (
  databaseId: string,
  targetModelId: string | undefined
) => {
  const findModel = useFindModel(databaseId);
  const findNamedGraph = useFindNamedGraph(databaseId);
  return React.useCallback(
    (modelsInfo: ModelsInfo, isConsolidating: boolean): string => {
      const countsByName: Record<string, number> = {};
      const countsByNamedGraph: Record<string, number> = {};
      let areThereModelsWithMissingOrInvalidNames = false;
      let areThereModelsWithMissingOrInvalidNamedGraphs = false;
      Object.values(modelsInfo).forEach((modelInfo) => {
        const { modelName, namedGraphIri, projectModelId, shouldPublish } =
          modelInfo;

        if (
          isConsolidating ? projectModelId !== targetModelId : !shouldPublish
        ) {
          return;
        }

        if (countsByName[modelName] === undefined) {
          countsByName[modelName] = 1;
        } else {
          countsByName[modelName] += 1;
        }

        if (countsByNamedGraph[namedGraphIri] === undefined) {
          countsByNamedGraph[namedGraphIri] = 1;
        } else {
          countsByNamedGraph[namedGraphIri] += 1;
        }

        if (
          !modelName ||
          !modelName.match(NAME_STARTS_WITH_MATCHER) ||
          modelName.match(INVALID_NAME_MATCHER) ||
          (modelInfo.creationType === CreationType.NEW && findModel(modelName))
        ) {
          areThereModelsWithMissingOrInvalidNames = true;
        }

        if (
          !namedGraphIri ||
          !namedGraphIri.match(IRI_STARTS_WITH_MATCHER) ||
          !namedGraphIri.includes(':') ||
          namedGraphIri.match(INVALID_IRI_MATCHER) ||
          // see `useValidateNamedGraphIri`
          (findNamedGraph(namedGraphIri) &&
            (modelInfo.creationType === CreationType.NEW ||
              (modelInfo.creationType === CreationType.REPLACE &&
                !findModel(modelInfo.modelName)?.namedGraphIris.includes(
                  modelInfo.namedGraphIri
                ))))
        ) {
          areThereModelsWithMissingOrInvalidNamedGraphs = true;
        }
      });

      if (areThereModelsWithMissingOrInvalidNames) {
        return copy.components.publishProjectDrawer.validation.modelNames
          .missingOrInvalidNames;
      }

      if (areThereModelsWithMissingOrInvalidNamedGraphs) {
        return copy.components.publishProjectDrawer.validation.modelNames
          .missingOrInvalidNamedGraphs;
      }

      // check if models to be created have overlapping names
      const areThereOverlappingNames = Object.values(countsByName).some(
        (count) => count > 1
      );

      if (areThereOverlappingNames) {
        return copy.components.publishProjectDrawer.validation.modelNames
          .overlappingNames;
      }

      // check if models have overlapping named graphs
      const areThereOverlappingNamedGraphs = Object.values(
        countsByNamedGraph
      ).some((count) => count > 1);

      if (areThereOverlappingNamedGraphs) {
        return copy.components.publishProjectDrawer.validation.modelNames
          .overlappingNamedGraphs;
      }

      return '';
    },
    [findModel, findNamedGraph, targetModelId]
  );
};

// named graphs

export const useFindNamedGraph = (databaseId: string) => {
  const namedGraphItems = useReduxSelector(namedGraphItemsSelector(databaseId));
  return React.useCallback(
    (iri: string) => namedGraphItems.find((item) => item.value === iri),
    [namedGraphItems]
  );
};

export const useModelNamedGraphPlaceholderByModelId = (databaseId: string) => {
  const findNamedGraph = useFindNamedGraph(databaseId);
  const modelsInfo = useDefaultModelsInfo(databaseId);

  return React.useMemo(
    () =>
      Object.entries(modelsInfo).reduce<Record<string, string>>(
        (acc, [modelId, modelInfo]) => {
          const namedGraphIriPlaceholder = findNamedGraph(
            modelInfo.namedGraphIri
          )
            ? copy.components.publishProjectDrawer.modelConfiguration.namedGraph
                .exampleText
            : modelInfo.namedGraphIri;
          acc[modelId] = namedGraphIriPlaceholder;

          return acc;
        },
        {}
      ),
    [findNamedGraph, modelsInfo]
  );
};

export const useCsvNamedGraphIriPlaceholder = (
  databaseId: string,
  fileUpload: ProjectFileUpload
) => {
  const findNamedGraph = useFindNamedGraph(databaseId);
  const { namedGraphIri } = useDefaultCsvMappingInfo(databaseId)(fileUpload);
  const namedGraphIriPlaceholder = findNamedGraph(namedGraphIri)
    ? ''
    : namedGraphIri;
  return namedGraphIriPlaceholder;
};

export const useValidateNamedGraphIri = (
  databaseId: string,
  targetModelId?: string,
  isConsolidating?: boolean,
  modelsInfo?: ModelsInfo
) => {
  const findModel = useFindModel(databaseId);
  const findNamedGraph = useFindNamedGraph(databaseId);

  return React.useCallback(
    (
      modelInfo: Pick<ModelInfo, 'creationType' | 'modelName' | 'namedGraphIri'>
    ): string => {
      if (!modelInfo.namedGraphIri) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .required;
      }

      if (!modelInfo.namedGraphIri.match(IRI_STARTS_WITH_MATCHER)) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .startsWith;
      }

      if (!modelInfo.namedGraphIri.includes(':')) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .semicolon;
      }

      const invalidChars = modelInfo.namedGraphIri.match(INVALID_IRI_MATCHER);
      if (invalidChars) {
        return copy.components.publishProjectDrawer.validation.namedGraph.characters(
          uniq(invalidChars)
        );
      }

      // show error if user typed existing named graph iri (on server or currently publishing) and...
      if (
        (findNamedGraph(modelInfo.namedGraphIri) ||
          Object.values(modelsInfo || {}).find(
            ({ modelName, namedGraphIri }) =>
              modelName !== modelInfo.modelName &&
              namedGraphIri === modelInfo.namedGraphIri
          )) &&
        // is a new model
        (modelInfo.creationType === CreationType.NEW ||
          // or is replacing an existing model, and modified the iri to be
          // an existing non-same graph
          (modelInfo.creationType === CreationType.REPLACE &&
            !findModel(modelInfo.modelName)?.namedGraphIris.includes(
              modelInfo.namedGraphIri
            )))
      ) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .unique;
      }

      if (modelsInfo) {
        const usedIris = new Set<string>();
        const isIriUsedMultipleTimes = Object.values(modelsInfo).some(
          (otherModelInfo) => {
            if (!otherModelInfo.namedGraphIri) {
              return false;
            }

            if (
              isConsolidating
                ? otherModelInfo.projectModelId !== targetModelId
                : !otherModelInfo.shouldPublish
            ) {
              return false;
            }

            if (
              usedIris.has(otherModelInfo.namedGraphIri) &&
              otherModelInfo.namedGraphIri === modelInfo.namedGraphIri
            ) {
              return true;
            }

            usedIris.add(otherModelInfo.namedGraphIri);
            return false;
          }
        );

        if (isIriUsedMultipleTimes) {
          return copy.components.publishProjectDrawer.validation.modelName
            .usedIri;
        }
      }

      return '';
    },
    [findModel, findNamedGraph, isConsolidating, modelsInfo, targetModelId]
  );
};

// virtual graphs

export const useFindVirtualGraph = (databaseId?: string) => {
  const virtualGraphItems = useReduxSelector(
    virtualGraphItemsSelector(databaseId)
  );
  return React.useCallback(
    (virtualGraphName: string) =>
      virtualGraphItems.find((item) => item.value === virtualGraphName),
    [virtualGraphItems]
  );
};

export const useVirtualGraphNamePlaceholder = (
  databaseId: string,
  virtualGraph: ProjectVirtualGraph
) => {
  const findVirtualGraph = useFindVirtualGraph();
  const { virtualGraphName } =
    useDefaultVirtualGraphMappingInfo(databaseId)(virtualGraph);
  const virtualGraphNamePlaceholder = findVirtualGraph(virtualGraphName)
    ? ''
    : virtualGraphName;
  return virtualGraphNamePlaceholder;
};

export const useValidateVirtualGraphName = () => {
  const findVirtualGraph = useFindVirtualGraph();
  return React.useCallback(
    (name: string): string => {
      if (!name) {
        return copy.components.publishProjectDrawer.validation.virtualGraph
          .required;
      }

      if (!name.match(NAME_STARTS_WITH_MATCHER)) {
        return copy.components.publishProjectDrawer.validation.virtualGraph
          .startsWith;
      }

      const invalidChars = name.match(INVALID_NAME_MATCHER);
      if (invalidChars) {
        return copy.components.publishProjectDrawer.validation.virtualGraph.characters(
          uniq(invalidChars)
        );
      }

      if (findVirtualGraph(name)) {
        return copy.components.publishProjectDrawer.validation.virtualGraph
          .unique;
      }

      return '';
    },
    [findVirtualGraph]
  );
};

// publish

export type PublishContextProps = {
  doExportProject: boolean;
  handleFinishedPublish: (date: number | null) => void;
  handlePendingPublish: (date: number | null) => void;
  isPending: boolean;
  setDoExportProject: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PublishContext = React.createContext<PublishContextProps>({
  doExportProject: false,
  handleFinishedPublish: noop,
  handlePendingPublish: noop,
  isPending: false,
  setDoExportProject: noop,
});

export const usePublishContext = () => {
  return React.useContext(PublishContext);
};

export type PublishOptions = {
  projectId: string;
  databaseInfo: DatabaseInfo;
  isConsolidatingModels: boolean;
  isPublishingRules: boolean;
  modelsInfo: ModelsInfo;
  modelAliasInfo?: ModelAliasInfo;
  prefixInfoArr: PrefixInfo[];
  ruleInfo: RulesInfo;
  spgInfo: SensitivePropertyGroupsInfo;
  csvMappingInfo?: CsvMappingInfoByProjectFileUploadId;
  vgMappingInfo?: VirtualGraphMappingInfoByProjectVgId;
};

export const usePublish = () => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const databasesById = useReduxSelector(
    (state) => state.databases.databasesById
  );
  const { doExportProject, handleFinishedPublish, handlePendingPublish } =
    usePublishContext();
  const isFileIriForImportSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isFileIriForImportSupported', connection)
  );
  const supportsDesignerPermissions = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDesignerPermissionsSupported',
      connection
    )
  );

  const { projectId } = useRouterParams();
  const virtualGraphsById = useReduxSelector(
    projectVirtualGraphsByIdSelector(projectId)
  );

  const { waitForEvaluatedQuestions } = useEvaluateQuestions();
  const verifyPublishPermissions = useVerifyPublishPermissions();

  return React.useCallback(
    async ({
      projectId,
      databaseInfo,
      isConsolidatingModels,
      isPublishingRules,
      modelAliasInfo,
      modelsInfo,
      prefixInfoArr,
      csvMappingInfo,
      vgMappingInfo,
      ruleInfo,
      spgInfo,
    }: PublishOptions): Promise<boolean> => {
      const modelsInfoForPublish = Object.entries(
        modelsInfo
      ).reduce<ModelsInfo>((acc, [key, modelInfo]) => {
        if (isConsolidatingModels || modelInfo.shouldPublish) {
          acc[key] = modelInfo;
        }
        return acc;
      }, {});

      const csvMappingInfoForPublish = csvMappingInfo
        ? Object.fromEntries(
            Object.entries(csvMappingInfo).filter(
              ([_, info]) => info.shouldPublish
            )
          )
        : undefined;

      const vgMappingInfoForPublish = vgMappingInfo
        ? Object.fromEntries(
            Object.entries(vgMappingInfo).filter(
              ([_, info]) => info.shouldPublish
            )
          )
        : undefined;

      // Check permissions before publishing if supported
      if (supportsDesignerPermissions) {
        const isCreatingDatabase = !databasesById[databaseInfo.databaseId];

        // Build map of VG ID -> data source ID for permission checks
        const dataSourceIdByVgId = vgMappingInfoForPublish
          ? Object.keys(vgMappingInfoForPublish).reduce<Record<string, string>>(
              (acc, vgId) => {
                const dataSourceId = virtualGraphsById[vgId]?.dataSourceId;
                if (dataSourceId) {
                  acc[vgId] = dataSourceId;
                }
                return acc;
              },
              {}
            )
          : {};

        const deniedPermission = verifyPublishPermissions(
          databaseInfo.databaseId,
          isCreatingDatabase,
          dataSourceIdByVgId,
          vgMappingInfoForPublish || {}
        );

        if (deniedPermission) {
          dispatch(
            projectsActionCreators.publishPermissionDenied(deniedPermission)
          );
          return false;
        }
      }

      const publishedDate = Date.now();
      handlePendingPublish(publishedDate);

      let didFinish = false;
      try {
        const succeeded = await dispatch(
          publishProject({
            projectId,
            publishedDate,
            databaseInfo,
            doExportProject,
            isConsolidatingModels,
            isFileIriForImportSupported,
            isPublishingRules,
            modelAliasInfo,
            modelsInfo: modelsInfoForPublish,
            prefixInfoArr,
            csvMappingInfo: csvMappingInfoForPublish,
            vgMappingInfo: vgMappingInfoForPublish,
            ruleInfo,
            spgInfo,
            waitForEvaluatedQuestions,
            // Pass the full, unfiltered selections so the publish history keeps an
            // entry for unchecked resources (with `shouldPublish: false`) instead
            // of dropping them, which would make them default back to checked.
            fullModelsInfo: modelsInfo,
            fullCsvMappingInfo: csvMappingInfo,
            fullVgMappingInfo: vgMappingInfo,
          })
        );

        // Always refresh permissions after a publish attempt — if the database was
        // created but a later step failed (partial failure), the user needs the new
        // database permissions for retry.
        if (connection) {
          dispatch(
            connectionAsyncActions.requestCurrentPermissions({
              username: connection.username,
            })
          );
        }

        if (succeeded) {
          handleFinishedPublish(publishedDate);
          didFinish = true;

          if (doExportProject) {
            await dispatch(exportProject(projectId, connection));
          }
          return true;
        }

        return false;
      } finally {
        // Ensure the pending/loading state is always cleared, even if a step above
        // throws after the project has been swapped — otherwise the Publish button
        // spins forever.
        if (!didFinish) {
          handlePendingPublish(null);
        }
      }
    },
    [
      connection,
      databasesById,
      dispatch,
      doExportProject,
      handleFinishedPublish,
      handlePendingPublish,
      isFileIriForImportSupported,
      supportsDesignerPermissions,
      verifyPublishPermissions,
      virtualGraphsById,
      waitForEvaluatedQuestions,
    ]
  );
};
